import { createApi } from '@reduxjs/toolkit/query/react';
import { apiAppointments } from 'tsx/constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

// Values already recorded on the appointment that the DEX tab's "Copy from appointment" action can
// offer to fill in - each key matches the DexDetailsBody field it would be copied into. A key is
// present only when the appointment holds a value AND the resolved field_tags call for that field.
export interface DexCopyableValues {
  trip_distance_km?: number;
  trip_travel_time_minutes?: number;
  engagement_time_minutes?: number;
}

export interface DexDetails {
  dex_outlet_activity_id: string | null;
  // Field-mapping signals resolved server-side from the appointment's stable dexServiceDEXID.
  field_tags: string[];
  is_excluded: boolean;
  dex_found: boolean;
  copyable: DexCopyableValues;
  subcontractor: boolean | null;
  volunteer: boolean | null;
  session_location: number | null;
  overnight: boolean | null;
  meals_provided: boolean | null;
  meals_cooked_offsite: boolean | null;
  texture_modified: boolean | null;
  meal_type_main: number | null;
  meal_type_light: number | null;
  meal_type_dessert: number | null;
  meal_type_beverage: number | null;
  meal_type_snack: number | null;
  engagement_time_minutes: number | null;
  trip_distance_km: number | null;
  trip_travel_time_minutes: number | null;
}

export type DexDetailsBody = Omit<
  DexDetails,
  'dex_outlet_activity_id' | 'field_tags' | 'is_excluded' | 'dex_found' | 'copyable'
>;

export const dexDetailsApi = createApi({
  reducerPath: 'dexDetailsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiAppointments }),
  tagTypes: ['dexDetails'],
  endpoints: (builder) => ({
    getDexDetails: builder.query<DexDetails | null, { appointmentId: string | number }>({
      query: ({ appointmentId }) => ({ url: `/${appointmentId}/dex-details`, method: 'GET' }),
      providesTags: (_result, _error, { appointmentId }) => [{ type: 'dexDetails', id: appointmentId }],
      transformResponse: (payload) => unwrap<DexDetails | null>(payload),
    }),
    upsertDexDetails: builder.mutation<
      DexDetails,
      {
        appointmentId: string | number;
        copied_fields?: string[];
        // Set when the values were copied from another appointment, so the save records where they
        // came from on the appointment's activity log rather than reading as manually entered.
        copied_from_appointment_id?: number | string | null;
      } & Partial<DexDetailsBody>
    >({
      query: ({ appointmentId, ...body }) => ({ url: `/${appointmentId}/dex-details`, method: 'PUT', body }),
      transformResponse: (payload) => unwrap<DexDetails>(payload),
      invalidatesTags: (_result, _error, { appointmentId }) => [{ type: 'dexDetails', id: appointmentId }],
    }),
  }),
});

export const { useGetDexDetailsQuery, useUpsertDexDetailsMutation } = dexDetailsApi;
